'use client';

import { useEffect, useRef } from 'react';

import Hyperbeam from '@hyperbeam/web';

const MAX_AREA_PIXELS = 1920 * 1080;
const RESIZE_DEBOUNCE_MS = 150; // Increased for better stability during rapid resize
const RESIZE_THRESHOLD = 5; // Increased threshold to prevent micro-adjustments

type HyperbeamInstance = Awaited<ReturnType<typeof Hyperbeam>>;

interface UseHyperbeamResizeProps {
  hyperbeamInstance: HyperbeamInstance | null;
  containerElement: HTMLDivElement | null;
}

export function useHyperbeamResize({
  hyperbeamInstance,
  containerElement,
}: UseHyperbeamResizeProps): void {
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const rafRef = useRef<number | null>(null);
  const pendingResizeRef = useRef<{ width: number; height: number } | null>(
    null
  );
  const previousDimensionsRef = useRef<{
    width: number;
    height: number;
  } | null>(null);
  const isResizingRef = useRef(false);

  useEffect(() => {
    if (!hyperbeamInstance || !containerElement) {
      return;
    }

    const resizeHyperbeam = (width: number, height: number) => {
      if (!hyperbeamInstance || !containerElement) return;

      // Ensure dimensions are valid and positive
      const validWidth = Math.max(1, Math.floor(width));
      const validHeight = Math.max(1, Math.floor(height));

      // Calculate area and constrain if needed
      const area = validWidth * validHeight;
      let finalWidth = validWidth;
      let finalHeight = validHeight;

      if (area > MAX_AREA_PIXELS) {
        // Calculate aspect-preserving dimensions within max area
        const aspectRatio = validWidth / validHeight;
        finalHeight = Math.floor(Math.sqrt(MAX_AREA_PIXELS / aspectRatio));
        finalWidth = Math.floor(finalHeight * aspectRatio);
      }

      // Ensure we don't exceed container bounds (safety check)
      const containerRect = containerElement.getBoundingClientRect();
      const maxWidth = Math.floor(containerRect.width);
      const maxHeight = Math.floor(containerRect.height);

      finalWidth = Math.min(finalWidth, maxWidth);
      finalHeight = Math.min(finalHeight, maxHeight);

      // Only resize if dimensions actually changed (with threshold to prevent micro-adjustments)
      const widthChanged =
        !previousDimensionsRef.current ||
        Math.abs(previousDimensionsRef.current.width - finalWidth) >
          RESIZE_THRESHOLD;
      const heightChanged =
        !previousDimensionsRef.current ||
        Math.abs(previousDimensionsRef.current.height - finalHeight) >
          RESIZE_THRESHOLD;

      if (widthChanged || heightChanged) {
        void hyperbeamInstance.resize(finalWidth, finalHeight);
        previousDimensionsRef.current = {
          width: finalWidth,
          height: finalHeight,
        };
      }
    };

    const performResize = () => {
      if (pendingResizeRef.current) {
        const { width, height } = pendingResizeRef.current;
        resizeHyperbeam(width, height);
        pendingResizeRef.current = null;
      }
      isResizingRef.current = false;
      rafRef.current = null;
    };

    // Initial resize using requestAnimationFrame
    const performInitialResize = () => {
      if (!containerElement) return;

      const rect = containerElement.getBoundingClientRect();
      const width = rect.width;
      const height = rect.height;

      if (width > 0 && height > 0) {
        resizeHyperbeam(width, height);
      }
    };

    // Use requestAnimationFrame to prevent flickering
    rafRef.current = requestAnimationFrame(() => {
      performInitialResize();

      // Set up ResizeObserver with debouncing
      const resizeObserver = new ResizeObserver((entries) => {
        // Store the latest dimensions
        for (const entry of entries) {
          const { width, height } = entry.contentRect;
          if (width > 0 && height > 0) {
            pendingResizeRef.current = { width, height };
          }
        }

        // Cancel any pending animation frame
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
        }

        // Clear previous debounce timer
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
          debounceTimerRef.current = null;
        }

        // Mark that we're in a resize operation
        isResizingRef.current = true;

        // Debounce resize calls - only execute after resize has stopped
        debounceTimerRef.current = setTimeout(() => {
          // Use requestAnimationFrame for smooth resizing
          rafRef.current = requestAnimationFrame(performResize);
        }, RESIZE_DEBOUNCE_MS);
      });

      resizeObserver.observe(containerElement);
      resizeObserverRef.current = resizeObserver;
    });

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
      pendingResizeRef.current = null;
      previousDimensionsRef.current = null;
      isResizingRef.current = false;
    };
  }, [hyperbeamInstance, containerElement]);
}
